// src/features/analytics/components/AnalyticsAudiencePage.tsx

import { useState } from 'react';
import { Group, Paper, SegmentedControl, Skeleton, Stack, Table } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import {
  ANALYTICS_DIMENSIONS,
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  type AnalyticsDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { HEATMAP_MAX_RANGE_DAYS, clampRangeSpan, rangeDays } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { useAnalyticsAudience } from '../useAnalyticsAudience';
import { useAnalyticsHeatmap } from '../useAnalyticsHeatmap';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import { ConcurrencyHeatmapCard } from './ConcurrencyHeatmapCard';

const DIMENSION_LABELS: Record<AnalyticsDimension, string> = {
  country: 'Country',
  deviceType: 'Device type',
  platform: 'Platform',
  streamType: 'Stream type',
};

const CHART_TOP_N = 12;

/** Watch time charts in whole minutes (counts chart as-is). */
function chartSeriesLabel(metric: AnalyticsMetric): string {
  return metric === 'watchTime' ? 'Watch time (min)' : ANALYTICS_METRIC_LABELS[metric];
}

function metricValue(row: { sessions: number; watchTimeMs: number; uniqueViewers: number }, metric: AnalyticsMetric): number {
  if (metric === 'sessions') return row.sessions;
  if (metric === 'uniqueViewers') return row.uniqueViewers;
  return row.watchTimeMs;
}

/** Account scope only (the layout hides/bounces this tab for network scope). */
export function AnalyticsAudiencePage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const accountCid = scope?.kind === 'account' ? scope.cid : null;

  const [dimension, setDimension] = useState<AnalyticsDimension>('country');
  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');

  const { audience, isLoading, isError, error, refetch } = useAnalyticsAudience(
    accountCid,
    dimension,
    range,
    metric
  );

  // The heatmap source is minute-grained presence: gateway cap is 90 days.
  const heatmapRangeWasClamped = rangeDays(range) > HEATMAP_MAX_RANGE_DAYS;
  const heatmapRange = clampRangeSpan(range, HEATMAP_MAX_RANGE_DAYS);
  const heatmapQuery = useAnalyticsHeatmap(accountCid, heatmapRange);

  const rows = audience?.data.rows ?? [];
  const chartData = rows.slice(0, CHART_TOP_N).map((row) => ({
    value: row.value,
    [chartSeriesLabel(metric)]:
      metric === 'watchTime' ? Math.round(row.watchTimeMs / 60000) : metricValue(row, metric),
  }));

  return (
    <Stack gap="md">
      <Group gap="sm" wrap="wrap">
        <SegmentedControl
          size="xs"
          data={ANALYTICS_DIMENSIONS.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
          value={dimension}
          onChange={(value) => setDimension(value as AnalyticsDimension)}
        />
        <SegmentedControl
          size="xs"
          data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
          value={metric}
          onChange={(value) => setMetric(value as AnalyticsMetric)}
        />
      </Group>

      {isError ? (
        <AnalyticsErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Skeleton height={260} />
      ) : rows.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={audience?.coverage}
          freshness={audience?.freshness}
          emptyMessage="No playback analytics for this range. Playback analytics begins with the new player rollout."
        />
      ) : (
        <Paper withBorder p="md">
          <AnalyticsCoverageNotice coverage={audience?.coverage} freshness={audience?.freshness} />
          <BarChart
            h={220}
            data={chartData}
            dataKey="value"
            series={[{ name: chartSeriesLabel(metric), color: 'blue.6' }]}
            withLegend={false}
            mt="xs"
          />
          <Table striped highlightOnHover mt="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{DIMENSION_LABELS[dimension]}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Sessions</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Watch time</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Unique viewers</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.value}>
                  <Table.Td>{row.value}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.sessions)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.uniqueViewers)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <ConcurrencyHeatmapCard
        heatmap={heatmapQuery.heatmap}
        isLoading={heatmapQuery.isLoading}
        isError={heatmapQuery.isError}
        error={heatmapQuery.error}
        onRetry={heatmapQuery.refetch}
        rangeWasClamped={heatmapRangeWasClamped}
      />
    </Stack>
  );
}
